import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { api, ApiError } from "../lib/api";
import { suggestEmails } from "../lib/emailDomains";
import { INFO_LINKS } from "./info/Info";
import { useI18n } from "../lib/i18n";
import type { MessageKey } from "../lib/messages";
import { SA_CITIES, citiesFor, cityName, onlyDigits, westernDigits } from "../lib/saudi";
import { useSession } from "../lib/session";
import { useAsync } from "../lib/useAsync";
import { IconBell, IconCall, IconChat, IconCheck, IconGlobe, IconHeart, IconMail, IconNext, IconPhone, IconPin, IconPlus, IconSearch, IconShield, IconUser, IconVerified, IconWhatsApp } from "../ui/icons";
import { AsyncButton, Empty, ErrorState, Field, Loading, Notice, PageHeader, Switch, useToast } from "../ui/kit";

type Tab = "profile" | "contact" | "settings";
const TABS: { key: Tab; label: MessageKey }[] = [
  { key: "profile", label: "profile.tab.profile" },
  { key: "contact", label: "profile.tab.contact" },
  { key: "settings", label: "profile.tab.settings" },
];

export function Account() {
  const { t, locale } = useI18n();
  const { me, loading } = useSession();
  const [params, setParams] = useSearchParams();
  const profile = useAsync(() => (me ? api<Profile>("/api/me/profile") : Promise.resolve(null)), [me?.id]);
  const [editing, setEditing] = useState<"public" | null>(null);
  if (loading) return <Loading />;
  if (!me) return <Navigate to="/signin?next=/account" replace />;

  // Every section sits one tap away under the header; the open tab lives in the URL (?tab=address).
  const tab = (TABS.some((x) => x.key === params.get("tab")) ? params.get("tab") : "profile") as Tab;
  const open = (next: Tab, edit: typeof editing = null) => {
    setParams(next === "profile" ? {} : { tab: next }, { replace: true });
    setEditing(edit);
  };

  const p = profile.data;
  // What a genuine, ready-to-trade profile needs. Each missing item is one tap away.
  const steps: { key: string; tab: Tab; done: boolean; label: MessageKey; open: () => void }[] = [
    { key: "name", tab: "profile", done: !isDefaultName(me.displayName), label: "profile.step.name", open: () => open("profile", "public") },
    { key: "city", tab: "profile", done: !!me.city, label: "profile.step.city", open: () => open("profile", "public") },
    { key: "email", tab: "profile", done: !!me.email, label: "profile.step.email", open: () => open("profile") },
    { key: "phone", tab: "profile", done: !!me.phone, label: "profile.step.phone", open: () => open("profile") },
    { key: "contact", tab: "contact", done: !!p && (p.contact.whatsapp || p.contact.calls), label: "profile.step.contact", open: () => open("contact") },
  ];
  const done = steps.filter((s) => s.done).length;
  const since = me.memberSince ? new Intl.DateTimeFormat(locale === "ar" ? "ar-SA-u-nu-latn-ca-gregory" : "en", { month: "long", year: "numeric" }).format(new Date(me.memberSince)) : null;

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      <PageHeader title={t("account.title")} />

      <section className="card">
        <div className="row" style={{ gap: "var(--s4)" }}>
          <div className="avatar" aria-hidden style={{ width: 52, height: 52, fontSize: 20 }}>{isDefaultName(me.displayName) ? <IconUser /> : initials(me.displayName)}</div>
          <div className="grow" style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: 19, margin: 0 }} dir="auto">{me.displayName}</h2>
            <div className="small muted">{[cityName(me.city, locale), since && t("profile.since", { d: since })].filter(Boolean).join(" · ")}</div>
            <div className="row wrap" style={{ marginTop: 6, gap: 6 }}>
              {me.email && <span className="pill good"><IconCheck size={12} /> {t("profile.emailVerified")}</span>}
              {me.phone && <span className="pill good"><IconCheck size={12} /> {t("profile.phoneVerified")}</span>}
              {me.sellerStatus === "verified" && <span className="pill good"><IconVerified size={12} /> {t("account.verified")}</span>}
            </div>
          </div>
        </div>
        {done < steps.length && (
          <div style={{ marginTop: "var(--s4)" }}>
            <div className="row between small">
              <strong>{t("profile.complete")}</strong>
              <span className="muted num">{t("profile.progress", { n: done, total: steps.length })}</span>
            </div>
            <div className="progress" style={{ marginTop: 6 }} aria-hidden>
              {steps.map((s) => <span key={s.key} className={s.done ? "done" : ""} />)}
            </div>
            <div className="chips" style={{ marginTop: "var(--s2)" }}>
              {steps.filter((s) => !s.done).map((s) => (
                <button key={s.key} type="button" className="chip" style={{ minHeight: 36 }} onClick={s.open}><IconPlus size={14} /> {t(s.label)}</button>
              ))}
            </div>
          </div>
        )}
      </section>

      <div className="seg" role="tablist" aria-label={t("account.title")} style={{ display: "flex", margin: "var(--s4) 0" }}>
        {TABS.map((x) => {
          const missing = steps.some((s) => s.tab === x.key && !s.done);
          return (
            <button key={x.key} role="tab" aria-selected={tab === x.key} aria-pressed={tab === x.key} style={{ flex: 1, justifyContent: "center", minHeight: 40 }} onClick={() => open(x.key)}>
              {t(x.label)}
              {missing && <span aria-hidden style={{ width: 6, height: 6, borderRadius: 3, background: "var(--danger)" }} />}
            </button>
          );
        })}
      </div>

      <div role="tabpanel">
        {tab === "profile" && <ProfileTab editing={editing === "public"} setEditing={(v) => setEditing(v ? "public" : null)} />}
        {tab === "contact" && (
          profile.error ? <ErrorState error={profile.error} onRetry={profile.reload} /> : !p ? <Loading rows={2} /> : <ContactTab contact={p.contact} reload={profile.reload} />
        )}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}

function ProfileTab({ editing, setEditing }: { editing: boolean; setEditing: (v: boolean) => void }) {
  const { t, locale } = useI18n();
  const { me } = useSession();
  if (!me) return null;
  return (
    <>
      {editing ? (
        <PublicForm onDone={() => setEditing(false)} />
      ) : (
        <div className="card">
          <div className="row between">
            <strong>{t("profile.public")}</strong>
            <button className="btn sm" onClick={() => setEditing(true)}>{t("profile.edit")}</button>
          </div>
          <div className="info-rows" style={{ borderTop: 0, marginTop: "var(--s2)" }}>
            <div className="info-row">
              <IconUser />
              <div className="t"><span>{t("account.name")}</span><b dir="auto">{me.displayName}</b></div>
            </div>
            <div className="info-row" style={{ borderBottom: 0 }}>
              <IconPin />
              <div className="t"><span>{t("account.city")}</span><b>{cityName(me.city, locale) || <span className="muted" style={{ fontWeight: 400 }}>{t("profile.notAdded")}</span>}</b></div>
            </div>
          </div>
        </div>
      )}
      <div className="card" style={{ marginTop: "var(--s3)" }}>
        <strong>{t("profile.contact")}</strong>
        <div className="small muted" style={{ margin: "4px 0 var(--s2)" }}>{t("profile.contactWhy")}</div>
        <ContactRow kind="email" value={me.email} />
        <ContactRow kind="phone" value={me.phone} />
      </div>
    </>
  );
}

/** How buyers can reach this person directly. Chat is always on; WhatsApp and calls are their choice. */
function ContactTab({ contact, reload }: { contact: Profile["contact"]; reload: () => unknown }) {
  const { t } = useI18n();
  const toast = useToast();
  const { me, refresh } = useSession();
  const [number, setNumber] = useState("");
  const [err, setErr] = useState<string | null>(null);
  // A new number for buyers needs a fresh code first, like sign-in details.
  const reauth = useReauth();
  if (!me) return null;
  const save = (next: { whatsapp: boolean; calls: boolean }, phone?: string) =>
    reauth.guard(async () => {
      await api("/api/me/contact-prefs", { method: "PUT", body: { ...next, ...(phone ? { phone } : {}) } });
      toast(t("contactPrefs.saved"));
      setNumber("");
      await reload();
    }, setErr);
  return (
    <>
      <div className="card stack">
        <strong>{t("contactPrefs.title")}</strong>
        <span className="small muted">{t("contactPrefs.why")}</span>
        <div className="toggle-row">
          <div className="row"><IconChat /> <div><div>{t("contactPrefs.chat")}</div><div className="small muted">{t("contactPrefs.chatHint")}</div></div></div>
          <span className="tag good">{t("contactPrefs.always")}</span>
        </div>
        <hr className="divider" style={{ margin: 0 }} />
        <div className="toggle-row">
          <div className="row"><IconWhatsApp /> <div><div>{t("contact.whatsapp")}</div><div className="small muted">{t("contactPrefs.whatsappHint")}</div></div></div>
          <Switch checked={contact.whatsapp} onChange={(v) => save({ whatsapp: v, calls: contact.calls }, number || undefined)} label={t("contact.whatsapp")} />
        </div>
        <hr className="divider" style={{ margin: 0 }} />
        <div className="toggle-row">
          <div className="row"><IconCall /> <div><div>{t("contact.call")}</div><div className="small muted">{t("contactPrefs.callsHint")}</div></div></div>
          <Switch checked={contact.calls} onChange={(v) => save({ whatsapp: contact.whatsapp, calls: v }, number || undefined)} label={t("contact.call")} />
        </div>
      </div>
      <div className="card stack" style={{ marginTop: "var(--s3)" }}>
        <strong>{t("contactPrefs.number")}</strong>
        {contact.number ? (
          <span className="row"><span className="num ltr">{contact.number}</span> <span className={`pill ${contact.numberVerified ? "good" : ""}`}>{t(contact.numberVerified ? "contact.numberVerified" : "contactPrefs.unverified")}</span></span>
        ) : (
          <span className="small muted">{t("contactPrefs.noNumber")}</span>
        )}
        {reauth.prompt}
        {!contact.numberVerified && !reauth.prompt && (
          <>
            <span className="small muted">{t("contactPrefs.typeNumber")}</span>
            <div className="row">
              <input className="input grow ltr" inputMode="tel" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="05X XXX XXXX" dir="ltr" />
              <AsyncButton className="btn" disabled={number.trim().length < 6} onClick={() => save({ whatsapp: contact.whatsapp, calls: contact.calls }, number.trim())}>{t("common.save")}</AsyncButton>
            </div>
            {!me.phone && <span className="tiny muted">{t("contactPrefs.verifyHint")}</span>}
          </>
        )}
        {err && <Notice kind="bad">{err}</Notice>}
        <span className="tiny muted"><IconShield size={14} /> {t("contactPrefs.private")}</span>
      </div>
      {me.sellerStatus === "unverified" || me.sellerStatus === "rejected" ? (
        <VerifyRequest onDone={refresh} />
      ) : (
        <div className="card row" style={{ marginTop: "var(--s3)" }}>
          <IconVerified /> {me.sellerStatus === "verified" ? t("account.verified") : t("account.verifyPending")}
        </div>
      )}
    </>
  );
}

function VerifyRequest({ onDone }: { onDone: () => Promise<unknown> | void }) {
  const { t } = useI18n();
  const [shop, setShop] = useState(false);
  const [name, setName] = useState("");
  const [cr, setCr] = useState("");
  const [vat, setVat] = useState("");
  const [error, setError] = useState<string | null>(null);
  return (
    <section className="card stack" style={{ marginTop: "var(--s3)" }}>
      <div className="row"><IconShield /> <strong>{t("verify.title")}</strong></div>
      <p className="small muted" style={{ margin: 0 }}>{t("verify.body")}</p>
      <label className="row" style={{ cursor: "pointer" }}>
        <input type="checkbox" checked={shop} onChange={(e) => setShop(e.target.checked)} style={{ width: 20, height: 20 }} />
        <span>{t("verify.isShop")}</span>
      </label>
      {shop && (
        <>
          <Field label={t("verify.businessName")}>{(id) => <input id={id} className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} dir="auto" />}</Field>
          <Field label={t("verify.cr")} hint={t("verify.crHint")}>{(id, d) => <input id={id} className="input" inputMode="numeric" value={cr} onChange={(e) => setCr(e.target.value)} maxLength={12} dir="ltr" aria-describedby={d} />}</Field>
          <Field label={t("verify.vat")} hint={t("verify.vatHint")} optional>{(id, d) => <input id={id} className="input" inputMode="numeric" value={vat} onChange={(e) => setVat(e.target.value)} maxLength={17} dir="ltr" aria-describedby={d} />}</Field>
        </>
      )}
      {error && <Notice kind="bad">{error}</Notice>}
      <AsyncButton
        className="btn sm"
        disabled={shop && (name.trim().length < 2 || cr.trim().length < 10)}
        onClick={async () => {
          setError(null);
          try {
            await api("/api/me/request-seller-verification", { body: { business: shop ? { name, crNumber: cr, vatNumber: vat.trim() || null } : null } });
            await onDone();
          } catch (e) {
            setError((e as ApiError).message);
          }
        }}
      >
        {t("verify.send")}
      </AsyncButton>
    </section>
  );
}

/** PDPL rights in the app: a copy of your data, and deleting the account. */
function YourData() {
  const { t } = useI18n();
  const { refresh } = useSession();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [check, setCheck] = useState<{ reservedDeals: number; openDeals: number } | null>(null);
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const blocked = !!check && check.reservedDeals > 0;
  return (
    <section className="card stack" style={{ marginTop: "var(--s5)" }} aria-labelledby="your-data">
      <strong id="your-data">{t("data.title")}</strong>
      <p className="small muted" style={{ margin: 0 }}>{t("data.body")}</p>
      <a className="btn sm" href="/api/me/export" download>{t("data.download")}</a>
      {!open ? (
        <button
          className="btn ghost sm"
          style={{ color: "var(--danger)" }}
          onClick={async () => {
            setOpen(true);
            setCheck(await api("/api/me/delete-check"));
          }}
        >
          {t("data.delete")}
        </button>
      ) : (
        <div className="stack">
          <Notice kind="warn">{t("data.deleteWarn")}</Notice>
          {blocked && <Notice kind="bad">{t("data.deleteBlocked", { n: check!.reservedDeals })}</Notice>}
          {!blocked && !!check?.openDeals && <Notice>{t("data.deleteOpenDeals", { n: check.openDeals })}</Notice>}
          {!blocked && (
            <>
              <Field label={t("data.typeDelete")}>
                {(id) => <input id={id} className="input" value={typed} onChange={(e) => setTyped(e.target.value)} dir="ltr" autoComplete="off" />}
              </Field>
              {error && <Notice kind="bad">{error}</Notice>}
              <AsyncButton
                className="btn danger sm"
                disabled={typed !== "DELETE"}
                onClick={async () => {
                  setError(null);
                  try {
                    await api("/api/me/delete", { body: { confirm: typed } });
                    await refresh();
                    nav("/");
                  } catch (e) {
                    setError((e as ApiError).message);
                  }
                }}
              >
                {t("data.deleteConfirm")}
              </AsyncButton>
            </>
          )}
          <button className="btn ghost sm" onClick={() => setOpen(false)}>{t("common.cancel")}</button>
        </div>
      )}
    </section>
  );
}

function SettingsTab() {
  const { t, locale, setLocale } = useI18n();
  const { me, refresh } = useSession();
  const nav = useNavigate();
  if (!me) return null;
  return (
    <>
      <div className="list">
        <div className="list-item">
          <IconGlobe /> <span className="grow">{t("lang.label")}</span>
          <div className="chips">
            <button className="chip" aria-pressed={locale === "en"} onClick={() => setLocale("en")} lang="en">English</button>
            <button className="chip" aria-pressed={locale === "ar"} onClick={() => setLocale("ar")} lang="ar">العربية</button>
          </div>
        </div>
        <Link className="list-item" to="/notifications">
          <IconBell /> <span className="grow">{t("account.notifications")}</span>
          {me.unreadNotifications > 0 && <span className="badge-dot" style={{ position: "static" }}>{me.unreadNotifications}</span>}
          <IconNext size={18} className="muted" />
        </Link>
        <Link className="list-item" to="/saved"><IconHeart /> <span className="grow">{t("account.favorites")}</span><IconNext size={18} className="muted" /></Link>
        <Link className="list-item" to="/saved?tab=searches"><IconSearch /> <span className="grow">{t("saved.searches")}</span><IconNext size={18} className="muted" /></Link>
        <Link className="list-item" to="/support"><IconChat /> <span className="grow">{t("account.support")}</span><IconNext size={18} className="muted" /></Link>
      </div>
      <div className="card" style={{ padding: 0, marginTop: "var(--s5)" }} aria-label={t("info.more")} role="navigation">
        {INFO_LINKS.map((l) => (
          <Link key={l.to} className="list-item" to={l.to} style={{ minHeight: 56 }}><span className="grow">{t(l.key)}</span><IconNext size={18} className="muted" /></Link>
        ))}
      </div>
      {me.staffRole && <StaffMfa />}
      {!me.staffRole && <YourData />}
      <button
        className="btn danger block"
        style={{ marginTop: "var(--s5)" }}
        onClick={async () => {
          await api("/api/auth/logout", { body: {} });
          await refresh();
          nav("/");
        }}
      >
        {t("account.signOut")}
      </button>
    </>
  );
}

type Profile = {
  contact: { whatsapp: boolean; calls: boolean; number: string | null; numberVerified: boolean };
};

/** New accounts are named "Member 1234" / "عضو 1234" until the person picks a name. */
const isDefaultName = (name: string) => /^(Member|عضو) \S+$/.test(name);
const initials = (name: string) => name.trim().split(/\s+/).slice(0, 2).map((w) => [...w][0]).join("").toUpperCase() || "?";
const errText = (e: unknown) => (e as ApiError).message;

function FormActions({ onCancel, onSave, disabled }: { onCancel: () => void; onSave: () => Promise<unknown>; disabled?: boolean }) {
  const { t } = useI18n();
  return (
    <div className="row" style={{ marginTop: "var(--s5)" }}>
      <AsyncButton className="btn primary" onClick={onSave} disabled={disabled}>{t("common.save")}</AsyncButton>
      <button type="button" className="btn ghost" onClick={onCancel}>{t("profile.cancel")}</button>
    </div>
  );
}

function CitySelect({ id, value, onChange, describedBy, invalid }: { id: string; value: string; onChange: (v: string) => void; describedBy?: string; invalid?: boolean }) {
  const { t, locale } = useI18n();
  const known = SA_CITIES.some((c) => c.en === value);
  return (
    <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)} aria-describedby={describedBy} aria-invalid={invalid || undefined} autoComplete="address-level2">
      <option value="" disabled>{t("profile.chooseCity")}</option>
      {value && !known && <option value={value}>{value}</option>}
      {citiesFor(locale).map((c) => <option key={c.en} value={c.en}>{c[locale]}</option>)}
    </select>
  );
}

function PublicForm({ onDone }: { onDone: () => void }) {
  const { t, locale } = useI18n();
  const { me, refresh } = useSession();
  const toast = useToast();
  const [name, setName] = useState(me && !isDefaultName(me.displayName) ? me.displayName : "");
  // Older accounts may hold a free-text city; map it onto the list when it matches.
  const [city, setCity] = useState(SA_CITIES.find((c) => c.en === me?.city || c.ar === me?.city)?.en ?? "");
  const [error, setError] = useState<string | null>(null);
  const clean = name.trim().replace(/\s+/g, " ");
  const nameProblem = !clean ? null : clean.length < 2 ? t("profile.nameShort") : /\d{6,}|@/.test(westernDigits(clean)) ? t("profile.nameContact") : null;
  return (
    <div className="card">
      <Field label={t("account.name")} hint={t("profile.nameHint")} error={nameProblem}>
        {(id, d) => <input id={id} className="input" value={name} onChange={(e) => setName(e.target.value)} aria-describedby={d} aria-invalid={!!nameProblem || undefined} maxLength={40} autoComplete="nickname" dir="auto" autoFocus />}
      </Field>
      <Field label={t("account.city")} hint={t("profile.cityHint")}>
        {(id, d) => <CitySelect id={id} value={city} onChange={setCity} describedBy={d} />}
      </Field>
      {clean.length >= 2 && (
        <div className="small muted" style={{ marginTop: "var(--s4)" }}>
          {t("profile.shownAs")} <strong dir="auto" style={{ color: "var(--ink)" }}>{clean}</strong>{city && ` · ${cityName(city, locale)}`}
        </div>
      )}
      {error && <div style={{ marginTop: "var(--s3)" }}><Notice kind="bad">{error}</Notice></div>}
      <FormActions
        onCancel={onDone}
        disabled={clean.length < 2 || !!nameProblem}
        onSave={async () => {
          setError(null);
          try {
            await api("/api/me", { method: "PATCH", body: { displayName: clean, ...(city ? { city } : {}) } });
            await refresh();
            toast(t("common.saved"));
            onDone();
          } catch (e) {
            setError(errText(e));
          }
        }}
      />
    </div>
  );
}

function ContactRow({ kind, value }: { kind: "email" | "phone"; value: string | null }) {
  const { t } = useI18n();
  const { refresh } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [contact, setContact] = useState("");
  const [challenge, setChallenge] = useState<{ challengeId: string; sentTo: string; devCode?: string } | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const isEmail = kind === "email";
  const suggestions = isEmail && !challenge ? suggestEmails(contact) : [];
  const reauth = useReauth();
  const reset = () => { setOpen(false); setContact(""); setChallenge(null); setCode(""); setError(null); };
  const send = () =>
    reauth.guard(async () => {
      setChallenge(await api(`/api/me/contact/${kind}/send`, { body: isEmail ? { email: contact } : { phone: contact } }));
      setCode("");
    }, setError);

  return (
    <div className="info-row" style={{ flexWrap: "wrap" }}>
      {isEmail ? <IconMail /> : <IconPhone />}
      <div className="t">
        <span>{t(isEmail ? "account.email" : "account.phone")}</span>
        {value ? <b className="ltr">{value}</b> : <b className="muted" style={{ fontWeight: 400 }}>{t("profile.notAdded")}</b>}
      </div>
      {value ? (
        <span className="pill good"><IconCheck size={12} /> {t("profile.verified")}</span>
      ) : !open ? (
        <button className="btn sm" onClick={() => setOpen(true)}><IconPlus size={16} /> {t("profile.add")}</button>
      ) : null}

      {open && !value && (
        <div style={{ flexBasis: "100%", paddingBottom: "var(--s3)" }}>
          {reauth.prompt}
          {reauth.prompt ? null : !challenge ? (
            <>
              <Field label={t(isEmail ? "signin.email" : "signin.phone")} hint={t(isEmail ? "signin.emailHint" : "signin.phoneHint")} error={error}>
                {(id, d) => (
                  <input
                    id={id}
                    className="input ltr"
                    type={isEmail ? "email" : "tel"}
                    inputMode={isEmail ? "email" : "tel"}
                    autoComplete={isEmail ? "email" : "tel"}
                    autoCapitalize="none"
                    spellCheck={false}
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    aria-describedby={d}
                    aria-invalid={!!error || undefined}
                    autoFocus
                  />
                )}
              </Field>
              {suggestions.length > 0 && (
                <div className="chips ltr">
                  {suggestions.map((s) => (
                    <button key={s.email} type="button" className="chip" onClick={() => setContact(s.email)}>
                      {s.kind === "typo" ? `${t("signin.didYouMean")} ${s.email}` : `@${s.email.split("@")[1]}`}
                    </button>
                  ))}
                </div>
              )}
              <div className="row" style={{ marginTop: "var(--s3)" }}>
                <AsyncButton className="btn primary sm" onClick={send} disabled={isEmail ? !/^\S+@\S+\.\S+$/.test(contact.trim()) : contact.length < 6}>{t("signin.sendCode")}</AsyncButton>
                <button type="button" className="btn ghost sm" onClick={reset}>{t("profile.cancel")}</button>
              </div>
            </>
          ) : (
            <>
              <Field label={t("signin.code")} hint={t("signin.sentTo", { p: challenge.sentTo })} error={error}>
                {(id, d) => (
                  <input id={id} className="input otp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(onlyDigits(e.target.value, 6))} aria-describedby={d} aria-invalid={!!error || undefined} autoFocus />
                )}
              </Field>
              {challenge.devCode && <Notice kind="warn">{t("signin.devCode", { c: challenge.devCode })}</Notice>}
              <div className="row" style={{ marginTop: "var(--s3)" }}>
                <AsyncButton
                  className="btn primary sm"
                  disabled={code.length !== 6}
                  onClick={() =>
                    reauth.guard(async () => {
                      await api(`/api/me/contact/${kind}/verify`, { body: { challengeId: challenge.challengeId, code, ...(isEmail ? { email: contact } : { phone: contact }) } });
                      await refresh();
                      toast(t("profile.contactAdded"));
                      reset();
                    }, setError)
                  }
                >
                  {t("profile.verify")}
                </AsyncButton>
                <button type="button" className="btn ghost sm" onClick={() => setChallenge(null)}>{t(isEmail ? "signin.changeEmail" : "signin.changeNumber")}</button>
                <AsyncButton className="btn ghost sm" onClick={send}>{t("signin.resend")}</AsyncButton>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

type ReauthChallenge = { challengeId: string; sentTo: string; devCode?: string };

/**
 * Sign-in details and the number buyers are sent to change only after a fresh code to the account's own e-mail or phone.
 * `guard(action, setError)` runs the action; when the server asks to confirm it's you, it sends that code, shows
 * `prompt` in place of the form, and runs the action again once the code is confirmed. Other errors go to setError.
 */
function useReauth() {
  const { t } = useI18n();
  const [pending, setPending] = useState<(ReauthChallenge & { retry: () => Promise<void> }) | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const guard = async (action: () => Promise<unknown>, onError: (msg: string | null) => void): Promise<void> => {
    onError(null);
    try {
      await action();
    } catch (e) {
      if ((e as ApiError).code !== "reauth_required") return onError(errText(e));
      try {
        const ch = await api<ReauthChallenge>("/api/me/reauth/send", { body: {} });
        setCode("");
        setError(null);
        setPending({ ...ch, retry: () => guard(action, onError) });
      } catch (e2) {
        onError(errText(e2));
      }
    }
  };

  const prompt = pending && (
    <div className="card" role="group" aria-label={t("reauth.title")}>
      <Field label={t("reauth.title")} hint={t("reauth.intro", { to: pending.sentTo })} error={error}>
        {(id, d) => (
          <input id={id} className="input otp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(onlyDigits(e.target.value, 6))} aria-describedby={d} aria-invalid={!!error || undefined} autoFocus />
        )}
      </Field>
      {pending.devCode && <Notice kind="warn">{t("signin.devCode", { c: pending.devCode })}</Notice>}
      <div className="row" style={{ marginTop: "var(--s3)" }}>
        <AsyncButton
          className="btn primary sm"
          disabled={code.length !== 6}
          onClick={async () => {
            setError(null);
            try {
              await api("/api/me/reauth/verify", { body: { challengeId: pending.challengeId, code } });
            } catch (e) {
              return setError(errText(e));
            }
            setPending(null);
            await pending.retry();
          }}
        >
          {t("reauth.confirm")}
        </AsyncButton>
        <button type="button" className="btn ghost sm" onClick={() => setPending(null)}>{t("profile.cancel")}</button>
      </div>
    </div>
  );
  return { guard, prompt };
}

function StaffMfa() {
  const { t } = useI18n();
  const { me, refresh } = useSession();
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  if (!me) return null;
  return (
    <section className="card stack" style={{ marginTop: "var(--s4)" }}>
      <strong>{t("account.staff")}</strong>
      {me.mfaVerified ? (
        <Link className="btn primary" to="/admin">{t("account.admin")}</Link>
      ) : (
        <>
          <div className="small muted">{t("mfa.body")}</div>
          {!me.totpEnabled && !secret && (
            <AsyncButton className="btn" onClick={async () => setSecret((await api<{ secret: string }>("/api/auth/mfa/setup", { body: {} })).secret)}>{t("mfa.setup")}</AsyncButton>
          )}
          {secret && (
            <div className="small">
              {t("mfa.secret")} <code className="ltr" style={{ wordBreak: "break-all", fontWeight: 700 }}>{secret}</code>
            </div>
          )}
          {(me.totpEnabled || secret) && (
            <>
              <Field label={t("mfa.code")} error={error}>
                {(id) => <input id={id} className="input otp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />}
              </Field>
              <AsyncButton
                className="btn primary"
                disabled={code.length !== 6}
                onClick={async () => {
                  setError(null);
                  try {
                    await api("/api/auth/mfa/verify", { body: { code } });
                    await refresh();
                  } catch (e) {
                    setError((e as ApiError).message);
                  }
                }}
              >
                {t("mfa.verify")}
              </AsyncButton>
            </>
          )}
        </>
      )}
    </section>
  );
}

type Notif = { id: string; type: string; title: string; body: string; link: string | null; read: boolean; createdAt: string };

export function Notifications() {
  const { t, relative } = useI18n();
  const { me, loading: sLoading, refresh } = useSession();
  const { data, error, loading, reload } = useAsync(() => (me ? api<{ notifications: Notif[] }>("/api/notifications") : Promise.resolve({ notifications: [] })), [me?.id]);
  if (!sLoading && !me) return <Navigate to="/signin?next=/notifications" replace />;
  const unread = data?.notifications.some((n) => !n.read);
  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      <PageHeader
        title={t("account.notifications")}
        back
        actions={unread ? <AsyncButton className="btn sm ghost" onClick={async () => { await api("/api/notifications/read", { body: {} }); await reload(); await refresh(); }}>{t("notif.markRead")}</AsyncButton> : null}
      />
      {loading && !data ? <Loading /> : error ? <ErrorState error={error} onRetry={reload} /> : !data!.notifications.length ? (
        <Empty title={t("notif.empty")} />
      ) : (
        <div className="list">
          {data!.notifications.map((n) => {
            const inner = (
              <div className="grow">
                <div className={`t ${n.read ? "" : "unread"}`} dir="auto">{n.title}</div>
                {n.body && <div className="s" dir="auto">{n.body}</div>}
                <div className="tiny muted">{relative(n.createdAt)}</div>
              </div>
            );
            return n.link?.startsWith("/") ? (
              <Link key={n.id} to={n.link} className="list-item">{inner}<IconNext size={18} className="muted" /></Link>
            ) : (
              <div key={n.id} className="list-item">{inner}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}
